// REL_1.1.15 F-262 / S-983 fix — marketing DeedoLogo now renders the REAL brand logo from the
// canonical brand CDN (mirrors deedo-frontend's components/marketing/DeedoLogo.tsx), replacing the
// REL_1.1.14 inline placeholder. Per Infra bug report 2026-05-29 (operator direction): reference
// the SAME asset from the SAME `branding` container the frontend uses (via VITE_BRAND_CDN_BASE_URL)
// — no bundled/second copy — so a single re-upload updates both sites. The inline SVG below is
// ONLY the error/404 fallback (e.g. fresh slot before the asset is uploaded).
//
// Render strategy: <img src={getBrandAsset(variant)}>; on 404/network error, onError swaps to the
// inline SVG fallback (uses var(--brand-*) tokens so it inherits the canonical palette).
import { useState } from "react";
import { getBrandAsset, type BrandAssetType } from "@/utils/getBrandAsset";

interface Props {
  size?: number;
  className?: string;
  /** Which brand-asset slot to fetch. Defaults to "logo-main" (light-bg). Use "logo-footer" on dark surfaces. */
  variant?: BrandAssetType;
  /** Tailwind colour utility for the stroke of the inline-SVG fallback only. */
  strokeClassName?: string;
  /** Tailwind colour utility for the inner D fill of the inline-SVG fallback only. */
  accentClassName?: string;
}

export default function DeedoLogo({
  size = 32,
  className = "",
  variant = "logo-main",
  strokeClassName = "text-[var(--brand-primary)]",
  accentClassName = "text-[var(--brand-primary)]",
}: Props) {
  const [cdnFailed, setCdnFailed] = useState(false);

  if (!cdnFailed) {
    return (
      <img
        src={getBrandAsset(variant)}
        alt="Deedo"
        width={size}
        height={size}
        className={className}
        onError={() => setCdnFailed(true)}
      />
    );
  }

  // Inline-SVG fallback — used when the CDN slot is empty / 404s / network blocked.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      aria-label="Deedo"
      role="img"
    >
      {/* House outline */}
      <path
        d="M5 14 L16 5 L27 14 L27 27 L5 27 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinejoin="round"
        className={strokeClassName}
      />
      {/* Inner D: vertical bar + half-circle bulge */}
      <g className={accentClassName} fill="currentColor">
        <rect x="11.5" y="14" width="2.25" height="10" rx="0.5" />
        <path d="M13.75 14 L18 14 A5 5 0 0 1 18 24 L13.75 24 Z" />
      </g>
      {/* Cyan dot accent inside the D */}
      <circle cx="17" cy="19" r="1.4" className="text-[var(--brand-secondary)]" fill="currentColor" />
    </svg>
  );
}
